import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './FriendsList.css';

const FriendsList = ({ user, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('friends'); // 'friends', 'requests'

  useEffect(() => {
    if (isOpen && user) {
      loadFriends();
    }
  }, [isOpen, user]);

  const loadFriends = async () => {
    try {
      setLoading(true);
      setError(null);

      // Import Supabase client directly
      const { supabase } = await import('../config/supabase');

      // Get user's friends from friendships table
      const { data: friendships, error: friendshipsError } = await supabase
        .from('friendships')
        .select('*')
        .or(`requester_id.eq.${user.sub},friend_id.eq.${user.sub}`)
        .eq('status', 'accepted');

      if (friendshipsError) throw friendshipsError;

      // Get pending friend requests (where current user is the friend_id, meaning others requested them)
      const { data: pendingRequests, error: requestsError } = await supabase
        .from('friendships')
        .select('*')
        .eq('friend_id', user.sub)
        .eq('status', 'pending');

      if (requestsError) throw requestsError;

      // Get friend user IDs
      const friendIds = friendships.map(friendship => 
        friendship.requester_id === user.sub ? friendship.friend_id : friendship.requester_id
      );

      // Get requester IDs for friend requests
      const requesterIds = pendingRequests.map(request => request.requester_id);

      // Get friend profiles
      let profiles = [];
      if (friendIds.length > 0) {
        const { data: friendProfiles, error: profilesError } = await supabase
          .from('profiles')
          .select('*')
          .in('user_id', friendIds);

        if (profilesError) throw profilesError;
        profiles = friendProfiles || [];
      }

      // Get profiles for friend requests
      let requestProfiles = [];
      if (requesterIds.length > 0) {
        const { data: requestProfilesData, error: requestProfilesError } = await supabase
          .from('profiles')
          .select('*')
          .in('user_id', requesterIds);

        if (requestProfilesError) throw requestProfilesError;
        requestProfiles = requestProfilesData || [];
      }

      // Get friends' current interests (active, non-expired)
      const { data: interests, error: interestsError } = await supabase
        .from('user_place_interests')
        .select(`
          *,
          places (
            name,
            address,
            location,
            rating,
            price_level
          )
        `)
        .in('user_id', friendIds)
        .eq('is_currently_interested', true)
        .gt('interest_expires_at', new Date().toISOString());

      if (interestsError) throw interestsError;

      // Get friends' favorite places
      const { data: favorites, error: favoritesError } = await supabase
        .from('user_place_interests')
        .select(`
          *,
          places (
            name,
            address,
            location,
            rating,
            price_level
          )
        `)
        .in('user_id', friendIds)
        .eq('is_favorite', true)
        .order('updated_at', { ascending: false })
        .limit(3); // Top 3 favorites per friend

      if (favoritesError) throw favoritesError;

      // Combine data for friends
      const enrichedFriends = profiles.map(profile => ({
        ...profile,
        currentInterests: interests.filter(interest => interest.user_id === profile.user_id),
        favoriteSpots: favorites.filter(favorite => favorite.user_id === profile.user_id),
        lastSeen: profile.last_login_at,
        isActive: profile.last_login_at && 
                 (new Date() - new Date(profile.last_login_at)) < (24 * 60 * 60 * 1000) // Active in last 24 hours
      }));

      // Sort by activity status, then by last seen
      enrichedFriends.sort((a, b) => {
        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;
        if (a.lastSeen && b.lastSeen) {
          return new Date(b.lastSeen) - new Date(a.lastSeen);
        }
        return 0;
      });

      // Prepare friend requests with request metadata
      const enrichedRequests = requestProfiles.map(profile => {
        const request = pendingRequests.find(req => req.requester_id === profile.user_id);
        return {
          ...profile,
          requestId: request.id,
          requestedAt: request.created_at,
          lastSeen: profile.last_login_at
        };
      });

      // Sort requests by most recent first
      enrichedRequests.sort((a, b) => {
        return new Date(b.requestedAt) - new Date(a.requestedAt);
      });

      setFriends(enrichedFriends);
      setFriendRequests(enrichedRequests);
    } catch (error) {
      console.error('Error loading friends:', error);
      setError('Failed to load friends');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProfile = (friendId) => {
    onClose();
    navigate(`/profile/${friendId}`);
  };

  const handleAcceptRequest = async (requestId, requesterId) => {
    try {
      const { supabase } = await import('../config/supabase');
      
      const { error } = await supabase
        .from('friendships')
        .update({ status: 'accepted' })
        .eq('id', requestId);

      if (error) throw error;

      // Reload data to reflect changes
      loadFriends();
    } catch (error) {
      console.error('Error accepting friend request:', error);
      setError('Failed to accept friend request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const { supabase } = await import('../config/supabase');
      
      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      // Reload data to reflect changes
      loadFriends();
    } catch (error) {
      console.error('Error rejecting friend request:', error);
      setError('Failed to reject friend request');
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never seen';
    
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 5) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return time.toLocaleDateString();
  };

  const getTimeRemaining = (expirationTime) => {
    if (!expirationTime) return null;
    
    const now = new Date();
    const expiry = new Date(expirationTime);
    const diff = expiry - now;
    
    if (diff <= 0) return null;
    
    const minutes = Math.floor(diff / (1000 * 60));
    return `${minutes}m left`;
  };

  const currentData = activeTab === 'friends' ? friends : friendRequests;

  if (!isOpen) return null;

  return (
    <div className="friends-list-overlay" onClick={onClose}>
      <div className="friends-list-panel" onClick={(e) => e.stopPropagation()}>
        <div className="friends-list-header">
          <h2>Friends</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="friends-tabs">
          <button 
            className={`tab ${activeTab === 'friends' ? 'active' : ''}`}
            onClick={() => setActiveTab('friends')}
          >
            All Friends ({friends.length})
          </button>
          <button 
            className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
            onClick={() => setActiveTab('requests')}
          >
            Friend Requests ({friendRequests.length})
          </button>
        </div>

        <div className="friends-list-content">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading your friends...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <div className="error-icon">😞</div>
              <p>{error}</p>
              <button onClick={loadFriends}>Try Again</button>
            </div>
          )}

          {!loading && !error && currentData.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">👋</div>
              <h3>
                {activeTab === 'friends' 
                  ? "No friends yet" 
                  : "No pending friend requests"
                }
              </h3>
              <p>
                {activeTab === 'friends'
                  ? "Add friends to see their favorite spots and coordinate meals together!"
                  : "When someone sends you a friend request, it will appear here."
                }
              </p>
            </div>
          )}

          {!loading && !error && currentData.length > 0 && (
            <div className={activeTab === 'friends' ? 'friends-grid' : 'requests-grid'}>
              {activeTab === 'friends' ? (
                // Render friends
                currentData.map(friend => (
                <div key={friend.user_id} className="friend-card">
                  <div className="friend-header">
                    <div className="friend-avatar-section">
                      <div className="friend-avatar-container">
                        <img 
                          src={friend.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(friend.display_name || friend.full_name || 'User')}&background=6366f1&color=ffffff`}
                          alt={friend.display_name || friend.full_name}
                          className="friend-avatar"
                        />
                        {friend.isActive && <div className="activity-indicator"></div>}
                      </div>
                      <div className="friend-info">
                        <h4>{friend.display_name || friend.full_name}</h4>
                        <span className="last-seen">{getTimeAgo(friend.lastSeen)}</span>
                      </div>
                    </div>
                    <button 
                      className="view-profile-btn"
                      onClick={() => handleViewProfile(friend.user_id)}
                      title="View Profile"
                    >
                      👤
                    </button>
                  </div>

                  {friend.bio && (
                    <div className="friend-bio">
                      "{friend.bio}"
                    </div>
                  )}

                  {friend.currentInterests.length > 0 && (
                    <div className="current-interests">
                      <div className="section-title">🍽️ Interested Right Now</div>
                      {friend.currentInterests.map(interest => (
                        <div key={interest.id} className="interest-item current">
                          <div className="place-info">
                            <span className="place-name">
                              {interest.place_name || interest.places?.name || 'Unknown Place'}
                            </span>
                            <span className="time-remaining">
                              {getTimeRemaining(interest.interest_expires_at)}
                            </span>
                          </div>
                          {interest.places?.rating && (
                            <div className="place-meta">
                              ⭐ {interest.places.rating}
                              {interest.places.price_level && 
                                ` • ${'$'.repeat(interest.places.price_level)}`
                              }
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {friend.favoriteSpots.length > 0 && (
                    <div className="favorite-spots">
                      <div className="section-title">❤️ Favorite Spots</div>
                      {friend.favoriteSpots.map(favorite => (
                        <div key={favorite.id} className="interest-item favorite">
                          <span className="place-name">
                            {favorite.place_name || favorite.places?.name || 'Unknown Place'}
                          </span>
                          {favorite.places?.rating && (
                            <div className="place-meta">
                              ⭐ {favorite.places.rating}
                              {favorite.places.price_level && 
                                ` • ${'$'.repeat(favorite.places.price_level)}`
                              }
                            </div>
                          )}
                          {favorite.notes && (
                            <div className="place-notes">"{favorite.notes}"</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                    {friend.currentInterests.length === 0 && friend.favoriteSpots.length === 0 && (
                      <div className="no-activity">
                        <span className="no-activity-text">No recent dining activity</span>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Render friend requests
                currentData.map(request => (
                  <div key={request.user_id} className="friend-request-card">
                    <div className="friend-header">
                      <div className="friend-avatar-section">
                        <div className="friend-avatar-container">
                          <img 
                            src={request.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(request.display_name || request.full_name || 'User')}&background=6366f1&color=ffffff`}
                            alt={request.display_name || request.full_name}
                            className="friend-avatar"
                          />
                        </div>
                        <div className="friend-info">
                          <h4>{request.display_name || request.full_name}</h4>
                          <span className="request-time">Requested {getTimeAgo(request.requestedAt)}</span>
                        </div>
                      </div>
                      <button 
                        className="view-profile-btn"
                        onClick={() => handleViewProfile(request.user_id)}
                        title="View Profile"
                      >
                        👤
                      </button>
                    </div>

                    {request.bio && (
                      <div className="friend-bio">
                        "{request.bio}"
                      </div>
                    )}

                    <div className="request-actions">
                      <button 
                        className="accept-btn"
                        onClick={() => handleAcceptRequest(request.requestId, request.user_id)}
                      >
                        ✅ Accept
                      </button>
                      <button 
                        className="reject-btn"
                        onClick={() => handleRejectRequest(request.requestId)}
                      >
                        ❌ Decline
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FriendsList; 